import {ApplicationData} from "../models/ApplicationData";
import {Beneficiary} from "../models/Beneficiary";
import {Application} from "../models/AppEntryData/Application";
import {PlanDetails} from "../models/PlanDetails";
import {CerberusResult, InsuredDetails} from "../models";
import {OwnerDetails} from "../models/OwnerDetails";
import {USState} from "../models/USState";
import {
    BaseApplicationData,
    CaseId,
    EzAppBaseUrl,
    NavigationStateStore,
    QuestionAnswerMeta,
    QuestionAnswers,
    StoredApplicationData, StoredApplicationDataList,
    StoredMedicalQuestions,
    SuspectedConditions
} from "./Store";
import {getEzAppToken} from "./Utilities";
import {UpdateApplicationRequest} from "../models/UpdateApplicationRequest";
import {get} from "svelte/store";
import {InsuranceType} from "../models/InsuranceType";
import {ValueType} from "../models/ValueType";
import {getBaseQuestionsFromService, retrieveSuspectedConditions} from "./QuestionDataService";
import {SuspectedCondition} from "../models/SuspectedCondition";
import {QuestionAnswer} from "../models/QuestionAnswer";
import {QuestionAnswerData} from "../models/QuestionAnswerData";
import {InforcePolicy} from "../models/InforcePolicy";
import {PendingPolicy} from "../models/PendingPolicy";
import {SetFatalError, SetIncidentalError} from "./ErrorService";
import {resetNavigationForApplication} from "./NavigationService";

export async function retrieveDataFromEzAppApi(appId: string) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + "Telelife/GetApplication";
    const response = await fetch (url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        },
        body:  JSON.stringify({
                'applicationId': appId
            }
        )
    });

    if (!response.ok) {
        SetFatalError();
        return new ApplicationData();
    } else {
        return response.json();
    }
}

export async function getCaseIdForPolicy(applicationId: string) {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/GetCaseIdForPolicy/' + applicationId;
    let result = new CerberusResult();
    await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            'Authorization': getEzAppToken()
        }
    })
        .then(response => response.json())
        .then(data => result = data)
        .catch(() => {
            SetFatalError();
        });

    return result;
}

export async function  updateApplication(request: UpdateApplicationRequest ) {
    try {
        const headers = {
            "Content-type": "application/json",
            'Authorization': getEzAppToken()
        }
        const baseUrl = get(EzAppBaseUrl);
        const  url = baseUrl + "Telelife/UpdateApplicationData";
        const response =  await fetch(url, {
            method: 'POST',
            body: JSON.stringify(request),
            cache: 'no-cache',
            headers
        });

        if (!response.ok) {
            SetIncidentalError();
        } else {
            return response.json();
        }
    }
    catch(error){
        SetIncidentalError();
    }
}

export async function getApplicationsForUser() {
    const baseUrl = get(EzAppBaseUrl);
    const url = baseUrl + 'Telelife/GetApplicationsForUser';
    const response = await fetch (url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        }
    });
    if (!response.ok) {
        SetFatalError();
    } else {
        return response.json();
    }
}

export async function submitApplication(applicationId: string) {
    const baseUrl = get(EzAppBaseUrl);
    const body = {'applicationId': applicationId};
    const url = baseUrl + "Telelife/SubmitApplication";
    await fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json',
            'Authorization': getEzAppToken()
        }
    }).then((response) => {
        if (!response.ok) {
            SetFatalError()
        } else {
            return response.json();
        }
    }).catch((error) => {
        throw error;
    });
}

export async function uploadFile(formData: FormData) {

    const url = get(EzAppBaseUrl) + "Telelife/UploadDLFile";
   const response = await  fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Authorization': getEzAppToken()
        },
        body: formData
    });

    if (!response.ok) {
        SetIncidentalError();
        //    return new ApplicationData();
    } else {
        return response.json();
    }
}
export function parseApplicationData(applicationData: ApplicationData): ApplicationData {
    const appData = new ApplicationData();
    appData.planDetails = parsePlanDetails(applicationData.planDetails);
    appData.insuredDetails = applicationData.insuredDetails;
     appData.ownerDetails = parseOwnerDetails(applicationData.ownerDetails);
    appData.applicationId = applicationData.applicationId;
    appData.pendingPolicies = applicationData.pendingPolicies;
    appData.inforcePolicies = applicationData.inforcePolicies;
    appData.beneficiaries = applicationData.beneficiaries;
    appData.isHipaaSignatureCompleted = applicationData.isHipaaSignatureCompleted;
    appData.isDriverLicenseUploaded = applicationData.isDriverLicenseUploaded;
    if (applicationData.beneficiaries) {
        appData.primaryBeneficiary = parsePrimaryBeneficiary(applicationData);
        appData.contingentBeneficiary = parseContingentBeneficiary(applicationData);
    }
    return appData;
}

function parsePlanDetails(details: PlanDetails): PlanDetails {
    const planDetails = new PlanDetails();
    planDetails.policyNumber = details.policyNumber;
    planDetails.issueState = details.issueState;
    planDetails.planName = details.planName;

    planDetails.riskClass = parseRiskClassType(details.riskClass);
    planDetails.faceAmount =  details.faceAmount;
    planDetails.premium = details.premium;

    if (details.payMode) {
        const numericPayMode = parseInt(details.payMode);
        if (!isNaN(numericPayMode)) {
            planDetails.payMode = parsePaymentType(numericPayMode);
        }
    } else {
        planDetails.payMode = '';
    }

    planDetails.policyStatus = details.policyStatus


    return planDetails;
}

function parseOwnerDetails(details: OwnerDetails): OwnerDetails {
    if (!details) return new OwnerDetails();
   // const ownerDetails = new OwnerDetails();
    let ownerDetails = details;
    ownerDetails.ownerTypeDisplay = parseOwnerType(details.ownerType);

    return ownerDetails;
}

function parsePrimaryBeneficiary(application: ApplicationData): Beneficiary {
    let bene = new Beneficiary();
    if (application.beneficiaries.length === 0) return bene;
    const primary = application.beneficiaries.find(b => b.isPrimary);

    if (primary) {
        bene = parseBeneficiary(primary);
    }

    return bene;
}

function parseContingentBeneficiary(application: ApplicationData): Beneficiary {
    let bene = new Beneficiary();
    if (application.beneficiaries.length === 0) return bene;
    const contingent = application.beneficiaries.find(b => !b.isPrimary);

    if (contingent) {
        bene = parseBeneficiary(contingent);
    }

    return bene;
}

function parseBeneficiary(bene: Beneficiary ): Beneficiary {
    const beneficiary = new Beneficiary();

    if (bene.beneficiaryType) {
        beneficiary.beneficiaryType = bene.beneficiaryType;
        const numericBeneType = bene.beneficiaryType;
        if (!isNaN(numericBeneType)) {
            beneficiary.beneficiaryTypeDescription = parseBeneficiaryType(numericBeneType);
        }
    }
    beneficiary.percentage = bene.percentage.toString() + '%';
    if (bene.beneficiaryRelationship) {
        const numericBeneficiaryRelationship = parseInt(bene.beneficiaryRelationship);
        if (!isNaN(numericBeneficiaryRelationship)) {
            beneficiary.relationship = parseBeneficiaryRelationshipType(numericBeneficiaryRelationship);
        }
    }

    return beneficiary;
}
export function updatePlanDetails(planDetails: PlanDetails): void {
    const appData = get(StoredApplicationData);
    const updatedData = {...appData, planDetails: planDetails }
    StoredApplicationData.set(updatedData);
}

export async function updateInsuredDetails(insuredDetails: InsuredDetails): Promise<void> {
    const appData = get(StoredApplicationData);
    const updatedData = {...appData, insuredDetails: insuredDetails}
    StoredApplicationData.set(updatedData);
    await  updateApplicationDataAsync(updatedData);
}

export async function updateOwnerDetails(ownerDetails: OwnerDetails): Promise<void> {
    const appData = get(StoredApplicationData);
    const updatedData = {...appData, ownerDetails: ownerDetails}
    StoredApplicationData.set(updatedData);
}
export async function updateBeneficiary(beneficiary: Beneficiary): Promise<void> {
    const appData = get(StoredApplicationData);
    const filteredBeneficiaries = appData.beneficiaries.filter(b => b.beneficiaryId !== beneficiary.beneficiaryId);
    filteredBeneficiaries.push(beneficiary);
    const updatedData = {...appData, beneficiaries: filteredBeneficiaries}
    StoredApplicationData.set(updatedData);
    await updateApplicationDataAsync(updatedData);
}

export async function updateInforcePolicy(inforcePolicy: InforcePolicy): Promise<void> {
    const appData = get(StoredApplicationData);
    const filteredInforcePolicy = appData.inforcePolicies.filter( x => x.inforcePolicyId !== inforcePolicy.inforcePolicyId);
    filteredInforcePolicy.push(inforcePolicy);
    const updatedData = {...appData, inforcePolicies: filteredInforcePolicy}
    StoredApplicationData.set(updatedData);
    await updateApplicationDataAsync(updatedData);
}

export async function updatePendingPolicy(pendingPolicy: PendingPolicy): Promise<void> {
    const appData = get(StoredApplicationData);
    const filteredPendingPolicy = appData.pendingPolicies.filter(x => x.pendingPolicyId !== pendingPolicy.pendingPolicyId);
    filteredPendingPolicy.push(pendingPolicy);
    const updatedData = {...appData, pendingPolicies: filteredPendingPolicy};
    StoredApplicationData.set(updatedData);
    await  updateApplicationDataAsync(updatedData);
}

export async function updateApplicationDataAsync(updatedData: ApplicationData): Promise<void> {
    let request = new UpdateApplicationRequest();
    request.insuredDetails = updatedData.insuredDetails;
    request.applicationId = updatedData.applicationId;
    request.ownerDetails = updatedData.ownerDetails;
    request.beneficiaries = updatedData.beneficiaries;
    request.inforcePolicies = updatedData.inforcePolicies;
    request.pendingPolicies = updatedData.pendingPolicies;
    await  updateApplication(updatedData);

}

function parsePaymentType(paymentType: number): string {
    switch (paymentType) {
        case 1:
            return 'Annual';
        case 2:
            return 'Semi-Annual';
        case 4:
            return 'Quarterly';
        case 12:
            return 'Monthly';
        default:
            return '';
    }
}
export function Suffixes(): string[] {
   return  ['I', 'II',   'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'Jr', 'Sr'];
}
function parseApplicationStatus(application: Application): string {
    if (!application || !application.statusStack) return '';

    let currentStatus = '';
    let maxDate = new Date(1900,1,1);
    application.statusStack.map(stack => {
        const stackDate = new Date(stack.changeDate);
        if (stackDate > (maxDate as Date)) {
            currentStatus = stack.currentStatus;
            maxDate = new Date(stack.changeDate);
        }
    });

    return currentStatus;
}

export function ParseInsuranceType(insuranceType: number) : string {
    if (!insuranceType) return '';
    switch (insuranceType) {
        case 1:
            return 'Term Life';
        case 2:
            return 'Whole Life';
        case 3:
            return 'Universal Life';
        case 4:
            return "AD&D";
        case 5:
            return 'Unknown';
        case 6:
            return 'Annuity';
        default:
            return '';
    }
}
export function getInsuranceTypes(): InsuranceType[] {
    return [
        {
            type: 'Term Life',
            key: 1
        },
        {
            type: 'Whole Life',
            key: 2
        },
        {
            type: 'Universal Life',
            key: 3
        },
        {
            type: 'AD&D',
            key: 4
        },
        {
            type: 'Unknown',
            key: 5
        },
        {
            type: 'Annuity',
            key: 6
        }
    ];
}
function parseRiskClassType(riskClassType: string): string {
    if (!riskClassType) return '';
    const numericRiskClass = parseInt(riskClassType);
    if (isNaN(numericRiskClass)) return '';

    switch (numericRiskClass) {
        case 1:
            return 'Select Preferred';
        case 2:
            return 'Preferred';
        case 3:
            return 'Standard';
        case 4:
            return 'Non-Tobacco';
        case 5:
            return 'Tobacco';
        case 6:
            return 'Substandard';
        case 7:
            return 'Preferred Tobacco';
        case 8:
            return 'Select';
        case 9:
            return 'Select Preferred Non-Tobacco';
        case 10:
            return 'SI - Preferred Non-Tobacco';
        case 11:
            return 'SI - Preferred Juvenile';
        case 12:
            return 'Standard Juvenile';
        case 13:
            return 'SI Non-Tobacco';
        case 14:
            return 'Standard Non-Tobacco';
        case 15:
            return 'SI - Standard Tobacco';
        case 16:
            return 'SI - Preferred Tobacco';
        case 17:
            return 'Preferred Non-Tobacco';
        case 18:
            return 'Standard Tobacco';
        case 19:
            return 'Executive';
        default:
            return '';
    }
}

function parseOwnerType(ownerType: string): string {
    if (!ownerType) return '';
    const numericOwnerType = parseInt(ownerType);
    if (isNaN(numericOwnerType)) return '';
    switch (numericOwnerType) {
        case 1:
            return 'Same As Insured';
        case 2:
            return 'Another Person';
        case 3:
            return 'Company';
        case 4:
            return 'Trust';
        case 5:
            return 'Joint Owner';
        default:
            return '';
    }
}

function parseBeneficiaryType(type: number): string {
    switch (type) {
        case 1:
            return 'Person';
        case 2:
            return 'Company';
        case 3:
            return 'Trust';
        case 4:
            return 'Estate of the Insured';
        case 5:
            return 'Surviving Children of the Insured - Shared Equally';
        case 6:
            return 'None';
        default: return '';
    }
}


export function getDataFromEzAppComplete(appData: any): void {
    appData = appData as ApplicationData;
    storeApplicationData(appData);
    resetNavigationForApplication(appData);

}

export function retrieveBaseMedicalQuestions(appData: ApplicationData) {
    getBaseMedicalQuestions(appData.applicationId)
        .then(questions => retrieveBaseMedicalQuestionsCompleted(questions, appData))
        .catch(() => {
            SetFatalError();
        });
}
export const applicationSelected = (applicationId: string) => {
    if (applicationId) {
        retrieveDataFromEzAppApi(applicationId)
            .then(appData => getDataFromEzAppComplete(appData));
    }
}

export function storeUserApplicationList(appList: any) {
    if (appList) {
        let applications: ApplicationData[] = [];
        appList.map(data => {
            applications.push(parseApplicationData(data));
        });
        StoredApplicationDataList.set(applications);
        applicationSelected(applications[0].applicationId);
    }
}

function retrieveBaseMedicalQuestionsCompleted(questions: any, appData: ApplicationData) {
    if (questions) {
        StoredMedicalQuestions.set(questions);
        getCaseId(appData);
        const navState = get(NavigationStateStore);
        navState.medicalQuestionsRetrievalComplete = true;
        NavigationStateStore.set(navState);

        getSuspectedConditions(appData);
    }
}


function getCaseId(appData: ApplicationData): void {
    if (appData) {
        appData = appData as ApplicationData;
        getCaseIdForPolicy(appData.applicationId)
            .then(data => CaseId.set(data.data));
    }
}

function getSuspectedConditions(appData: ApplicationData): void {
    if (appData) {
        appData = appData as ApplicationData;
        retrieveSuspectedConditions(appData.applicationId)
            .then(data => SuspectedConditions.set(data as SuspectedCondition[]));
    }
}

function storeApplicationData(appData: any): void {
    StoredApplicationData.set(parseApplicationData(appData));
    BaseApplicationData.set(appData);
}

const getBaseMedicalQuestions = async(applicationId) =>  {
    let questions = await getBaseQuestionsFromService(applicationId);
    let questionAnswers: QuestionAnswer[] = [];
    let totalQuestions = 0;
    let questionsAnswered = 0;

    questions.map(page => {
        if (page.questions.length > 0) {
            page.questions.map(q => {
                if (!q.code.startsWith('Header')) {
                    let value = '';
                    totalQuestions++;
                    if (q.value)
                    {
                        value = q.value.toString();
                        questionsAnswered++;
                    }
                    let answer = new QuestionAnswer(q.code.toString(), value);
                    questionAnswers.push(answer);
                }

            });
        }
    });

    QuestionAnswers.set(questionAnswers);
    const questionAnswerData = new QuestionAnswerData(totalQuestions, questionsAnswered);
    QuestionAnswerMeta.set(questionAnswerData);
    return questions;
}



export function getGender(genderKey: number): string {

       switch(genderKey) {
           case 1:
               return "Male";

           case 2:
               return "Female";
           default:
               return "";
         }
}

export function getMaritalStatus(key: number): string {

        switch(key) {
            case 1:
                return "Married";
            case 2:
                return "Single";
            default:
                return "";
        }

}

export function getLengthOfResidenceTypes(): string[] {
    return [
        'Less than 1 Year', '1 year', '2 years', '3 years', '4 years', '5 years', '6 years', '7 years', '8 years', '9 years', '10 years', 'Over 10 years'
    ];
}

export function getBeneficiaryRelationshipTypes(): ValueType[] {
    return [
         {
             key: 1,
             type: "Wife"
         },
         {
             key: 2,
             type: "Husband"
         },
         {
             key: 3,
             type: "Daughter"
         },
         {
             key: 4,
             type: "Son"
         },

         {
             key: 5,
             type: "Mother"
         },
         {
             key: 6,
             type: "Father"
         },
         {
             key: 7,
             type: "Aunt"
         },
         {
             key: 8,
             type: "Brother"
         },
         {
             key: 9,
             type: "Brother-in-law"
         },
         {
             key: 10,
             type: "Business"
         },
         {
             key: 11,
             type: "Business Associate"
         },
         {
             key: 12,
             type: "Charity"
         },
         {
             key: 13,
             type: "Cousin"
         },
         {
             key: 14,
             type: "Creditor"
         },
         {
             key: 15,
             type: "Custodian"
         },
         {
             key: 16,
             type: "Daughter-in-Law"
         },
         {
             key: 17,
             type: "Employee"
         },
         {
             key: 18,
             type: "Employer"
         },
         {
             key: 19,
             type: "Estate"
         },
         {
             key: 20,
             type: "Ex-husband"
         },
         {
             key: 21,
             type: "Ex-wife"
         },
         {
             key: 22,
             type: "Father-in-law"
         },
         {
             key: 23,
             type: "Fiancée"
         },
         {
             key: 24,
             type: "Friend"
         },
         {
             key: 25,
             type: "Granddaughter"
         },
         {
             key: 26,
             type: "Grandfather"
         },
         {
             key: 27,
             type: "Grandmother"
         },
         {
             key: 28,
             type: "Grandson"
         },
         {
             key: 29,
             type: "Guardian"
         },
         {
             key: 30,
             type: "Mother-in-law"
         },
         {
             key: 31,
             type: "Nephew"
         },
         {
             key: 32,
             type: "Niece"
         },
         {
             key: 33,
             type: "Other"
         },
         {
             key: 34,
             type: "Self"
         },
         {
             key: 35,
             type: "Significant Other"
         },
         {
             key: 36,
             type: "Sister-in-Law"
         },
         {
             key: 37,
             type: "Sister"
         },
         {
             key: 38,
             type: "Son-in-Law"
         },
         {
             key: 39,
             type: "Spouse"
         },
         {
             key: 40,
             type: "Step-daughter"
         },
         {
             key: 41,
             type: "Step-father"
         },
         {
             key: 42,
             type: "Step-mother"
         },
         {
             key: 43,
             type: "Step-son"
         },
         {
             key: 44,
             type: "Trust"
         },
         {
             key: 45,
             type: "Uncle"
         },
         {
             key: 46,
             type: "Unknown"
         },
     ];
}
export function parseBeneficiaryRelationshipType(type: number): string {
    switch (type) {
        case 1:
            return "Wife";
        case 2:
            return "Husband";
        case 3:
            return "Daughter";
            case 4:
                return "Son";
            case 5:
                return "Mother";
            case 6:
                return "Father";
            case 7:
                return "Aunt";
            case 8:
                return "Brother";
            case 9:
                return "Brother-in-law";
            case 10:
                return "Business";
            case 11:
                return "Business Associate";
            case 12:
                return "Charity";
            case 13:
                return "Cousin";
            case 14:
                return "Creditor";
            case 15:
                return "Custodian";
            case 16:
                return "Daughter-in-Law";
            case 17:
                return "Employee";
            case 18:
                return "Employer";
            case 19:
                return "Estate";
            case 20:
                return "Ex-husband";
            case 21:
                return "Ex-wife";
            case 22:
                return "Father-in-law";
            case 23:
                return "Fiancée";
            case 24:
                return "Friend";
            case 25:
                return "Granddaughter";
            case 26:
                return "Grandfather";
            case 27:
                return "Grandmother";
            case 28:
                return "Grandson";
            case 29:
                return "Guardian";
            case 30:
                return "Mother-in-law";
            case 31:
                return "Nephew";
            case 32:
                return "Niece";
            case 33:
                return "Other";
            case 34:
                return "Self";
            case 35:
                return "Significant Other";
            case 36:
                return "Sister-in-Law";
            case 37:
                return "Sister";
            case 38:
                return "Son-in-Law";
            case 39:
                return "Spouse";
            case 40:
                return "Step-daughter";
            case 41:
                return "Step-father";
            case 42:
                return "Step-mother";
            case 43:
                return "Step-son";
            case 44:
                return "Trust";
            case 45:
                return "Uncle";
            case 46:
                return "Unknown";
        default:
            return '';
    }
}
export function getAllUSStates(): USState[] {
    return [
        {
            name: 'Alabama',
            abbreviation: 'AL'
        },
        {
            name: 'Alaska',
            abbreviation: 'AK'
        },
        {
            name: 'Arkansas',
            abbreviation: 'AR'
        },
        {
            name: 'Arizona',
            abbreviation: 'AZ'
        },
        {
            name: 'California',
            abbreviation: 'CA'
        },
        {
            name: 'Colorado',
            abbreviation: 'CO'
        },
        {
            name: 'Connecticut',
            abbreviation: 'CT'
        },
        {
            name: 'District of Columbia',
            abbreviation: 'DC'
        },
        {
            name: 'Delaware',
            abbreviation: 'DE'
        },
        {
            name: 'Florida',
            abbreviation: 'FL'
        },
        {
            name: 'Georgia',
            abbreviation: 'GA'
        },
        {
            name: 'Hawaii',
            abbreviation: 'HI'
        },
        {
            name: 'Iowa',
            abbreviation: 'IA'
        },
        {
            name: 'Idaho',
            abbreviation: 'ID'
        },
        {
            name: 'Illinois',
            abbreviation: 'IL'
        },
        {
            name: 'Indiana',
            abbreviation: 'IN'
        },
        {
            name: 'Kansas',
            abbreviation: 'KS'
        },
        {
            name: 'Kentucky',
            abbreviation: 'KY'
        },
        {
            name: 'Louisiana',
            abbreviation: 'LA'
        },
        {
            name: 'Massachusetts',
            abbreviation: 'MA'
        },
        {
            name: 'Maryland',
            abbreviation: 'MD'
        },
        {
            name: 'Maine',
            abbreviation: 'ME'
        },
        {
            name: 'Michigan',
            abbreviation: 'MI'
        },
        {
            name: 'Minnesota',
            abbreviation: 'MN'
        },
        {
            name: 'Missouri',
            abbreviation: 'MO'
        },
        {
            name: 'Mississippi',
            abbreviation: 'MS'
        },
        {
            name: 'Montana',
            abbreviation: 'MT'
        },

        {
            name: 'Nebraska',
            abbreviation: 'NE'
        },
        {
            name: 'Nevada',
            abbreviation: 'NV'
        },
        {
            name: 'New Hampshire',
            abbreviation: 'NH'
        },
        {
            name: 'New Jersey',
            abbreviation: 'NJ'
        },
        {
            name: 'New Mexico',
            abbreviation: 'NM'
        },
        {
            name: 'New York',
            abbreviation: 'NY'
        },
        {
            name: 'North Carolina',
            abbreviation: 'NC'
        },
        {
            name: 'North Dakota',
            abbreviation: 'ND'
        },
        {
            name: 'Ohio',
            abbreviation: 'OH'
        },
        {
            name: 'Oklahoma',
            abbreviation: 'OK'
        },
        {
            name: 'Oregon',
            abbreviation: 'OR'
        },
        {
            name: 'Pennsylvania',
            abbreviation: 'PA'
        },
        {
            name: 'Puerto Rico',
            abbreviation: 'PR'
        },
        {
            name: 'Rhode Island',
            abbreviation: 'RI'
        },
        {
            name: 'South Carolina',
            abbreviation: 'SC'
        },
        {
            name: 'South Dakota',
            abbreviation: 'SD'
        },
        {
            name: 'Tennessee',
            abbreviation: 'TN'
        },
        {
            name: 'Texas',
            abbreviation: 'TX'
        },
        {
            name: 'Utah',
            abbreviation: 'UT'
        },
        {
            name: 'Vermont',
            abbreviation: 'VT'
        },
        {
            name: 'Virginia',
            abbreviation: 'VA'
        },
        {
            name: 'Washington',
            abbreviation: 'WA'
        },
        {
            name: 'West Virginia',
            abbreviation: 'WV'
        },
        {
            name: 'Wisconsin',
            abbreviation: 'WI'
        },
        {
            name: 'Wyoming',
            abbreviation: 'WY'
        }
    ];
}

export function getGovIdDisplayed(govId: string): string
{
    const len = govId ? govId.length : 0;
    if (len >= 4) {
        const start = len - 4
        return govId.substring(start, len);
    } else {
        return govId;
    }
}




export function getReplacementCompanies(): string[] {
    return [
    '20th Century Life',
    '4 Ever Life',
    '5 Star Life Insurance Company',
    'A I Life',
    'AAA Life Insurance Company',
    'AAFMAA',
    'AAFP New York Life',
    'AAL Branch',
    'AARP',
    'Abraham Lincoln Insurance Co',
    'Abundant Life Ins Co',
    'ACA Assurance',
    'Acacia Life',
    'Acacia Life Insurance Company',
    'Academy Life Insurance Co',
    'Acadian Life Ins Co',
    'Accordia Life & Annuity Co',
    'Accordia Life Policyholder Services',
    'Accordia/Athene',
    'ACS Member Insurance Program',
    'ADA Members Insurance Plans',
    'ADI Trust Fund',
    'ADP Total Source',
    'Advance Insurance Company',
    'Advanced Insurance Co of KS',
    'Advantage Insurance',
    'Advisors Excel',
    'AEGON',
    'Aetna Life Insurance',
    'Aetna Life Insurance and Annuity Co.',
    'Aetna Voluntary Plans',
    'AFBA 5 Star Life Ins Co',
    'AFBA-Armed Forces Benefit Association',
    'Affinion Group',
    'Affinity Insurance',
    'Affinity Life',
    'AFLAC',
    'AFSA',
    'AGIA',
    'AGL Life Assurance Company',
    'AgStar Financial Services',
    'AICPA',
    'AICPA-AON',
    'AIG',
    'AIG Annuity',
    'AIG Direct',
    'AIG Life Insurance Company',
    'AIG SunAmerica',
    'AIP',
    'Alabama Reassurance Company',
    'Alexander Hamilton Life Insurance',
    'Alfa Life Insurance Company',
    'All America Financial',
    'All American Life',
    'Alliance Health and Life Insurance Co.',
    'Alliance One Life Ins Co',
    'Alliant Insurance',
    'Allianz Life Co. of North America',
    'Allianz Life Ins Co',
    'Allianz Life Ins Co of NA',
    'Allied Life Insurance',
    'Allmerica Financial',
    'Allmerica Financial Life Ins & Annuity',
    'Allscripts Life Insurance',
    'AllSouth Insurance Group',
    'Allstate',
    'Allstate Life Insurance Co',
    'ALPA (Air Line Pilots Associates)',
    'Alpha Insurance Services, Inc.',
    'Alumni Life',
    'AMA Insurance Agency, Inc.',
    'Amalgamated Life and Health Insurance',
    'America First Credit Union',
    'American Agency Life',
    'American Agency Life Ins Co.',
    'American Amicable Life Ins. Co.',
    'American Assoc of Orthodontist',
    'American Association of Retired People',
    'American Bankers Life Assurance Co.',
    'American Bar Association',
    'American Bar Endowment',
    'American Benefit Life Insurance Company',
    'American Centurion Life Assur',
    'American Century Life Insurance Company of TX',
    'American Community Life Ins',
    'American Continental Ins Co',
    'American Continental Life',
    'American Enterprise Life',
    'American Equity',
    'American Express',
    'American Family Assurance Co of Columbus',
    'American Family Insurance',
    'American Family Life Assur Co of NY',
    'American Family Life Ins Co.',
    'American Family Life Insurance Co',
    'American Farm Life',
    'American Farmers & Ranchers Mutual Insurance',
    'American Fidelity Assurance',
    'American Fidelity Life Insurance Company',
    'American Financial Life',
    'American Foundation Life Ins Co',
    'American Founders Life',
    'American Franklin Life Insurance Company',
    'American Funds Service Company',
    'American General Annuity Ins Co',
    'American General Assurance Co',
    'American General Life',
    'American General Life & Accident Insurance Company (TN)',
    'American General Life (TX)',
    'American General Life Ins Co',
    'American Guardian',
    'American Health & Life Ins Co',
    'American Heritage Life',
    'American Home Life',
    'American Income Life',
    'American Independent Life',
    'American Ins Co of Texas',
    'American Institute of CPAs',
    'American Insurance Admin',
    'American International Life Assurance Company of New York',
    'American Investors Life',
    'American Legion Life Plans',
    'American Life & Casualty Ins Co',
    'American Life & Security Corp',
    'American Life Insurance Co of NY',
    'American Life Insurance Co. (ALICO)',
    'American Life Insurance North American Company',
    'American Life of NY',
    'American Mayflower Life Insurance Company',
    'American Medical Association',
    'American Memorial Life',
    'American Memorial Life Ins Co',
    'American Merchants Life',
    'American Modern Insurance Group',
    'American Mutual Life Ins Co',
    'American Nat\'l Insurance Co',
    'American National Life Ins Co of NY',
    'American National Property and Casualty Co',
    'American Physicians Life',
    'American Pioneer Life Ins Company',
    'American Preferred Insurance',
    'American Pride Insurance',
    'American Progressive',
    'American Progressive Life and Health Ins Co of NY',
    'American Public Life Ins Co',
    'American Republic Ins Co',
    'American Savings Life Insurance Company',
    'American Skandia',
    'American Star Ins Services',
    'American States Life',
    'American Trust Insurance Services',
    'American Underwriters Life Group',
    'American Union Life Ins Co',
    'American United Life',
    'American-Amicable Life',
    'Americo Financial',
    'Americo Life Insurance',
    'Americom Life and Annuity Ins Company',
    'Americorp Insurance Group',
    'AmeriLife Group',
    'Ameriprise Financial Services',
    'Ameriprise Financial Services Inc',
    'Amerisure Insurance',
    'Ameritas Variable Life Ins.',
    'Amerus Life',
    'Amex Life Assurance Company',
    'Amica Life Ins Co',
    'Amoco Life Insurance Co',
    'Amway Distribution Benefits Group',
    'AmWINS Group Inc',
    'Anchor National Life Ins Co',
    'Annuity & Life Reassurance, Inc',
    'Annuity Board, SBC',
    'Anpac',
    'Anthem Blue Cross',
    'Anthem Life Insurance Co',
    'AON Insurance Service',
    'AON thru AICPA',
    'Aopa Life',
    'Apache Corporation',
    'APSIT (American Physical Society Ins Trust)',
    'Arbella Insurance Group',
    'Arcadia Life Insurance',
    'Armed Forces Benefit Association',
    'Armed Services Mutual Benefit Association',
    'Army & Air Force Mutual Aid Association',
    'ASCE Member Insurance Program',
    'Ashley Life Ins Co',
    'ASME',
    'ASME Ins Prog Admin',
    'Associates Financial Life Insurance Co',
    'Association of Insurance Professionals',
    'Assurance',
    'Assurant Group',
    'Assurant Solutions',
    'Assured Investors Life Ins Co',
    'Assured Security Life Ins Co',
    'Assurity Life Ins Co',
    'Athene Annuity & Life',
    'Athene Annuity and Life Assurance Company',
    'Athene USA',
    'Atlanta Life Financial Group',
    'Atlantic Coast Life Ins Co',
    'Atlantic Southern Insurance Co',
    'Aurora National Life Ins Co',
    'Auto Club Life Insurance Co',
    'Auto-Owners Life Insurance Co',
    'Automobile Club of Southern California',
    'Aviva-Athene - Alliance One',
    'AVL Life Insurance Co',
    'AVMA',
    'AVMA GHLIT',
    'Avon Insurance Associates',
    'AXA Equitable',
    'AXA Financial, Inc.',
    'Axis Insurance Agency',
    'Baer Insurance Services',
    'Balboa Life Ins Co',
    'Baltimore Life Insurance Company',
    'Banc of America',
    'Banc One Insurance Group',
    'Bank of America',
    'Bank of the West',
    'Bankers and Farmers Life Ins Co',
    'Bankers Fidelity Life Insurance Co',
    'Bankers Life & Casualty Co',
    'Bankers Life Ins Co',
    'Bankers Mutual Life Ins Co',
    'Bankers National Life',
    'Bankers Security Life Society',
    'Bankers United Life Insurance Company',
    'Banner Life Insurance Company',
    'Baptist Life Association',
    'Barnett & Associates',
    'Bay State Insurance Company',
    'BB & T Corporation',
    'Beall Insurance',
    'Bear River Mutual Insurance',
    'Beneficial Life Insurance',
    'Benefit Consultants, Inc.',
    'Benton Insurance',
    'Berkshire Life Insurance Co.',
    'Berkshire Life Insurance Company',
    'Best Life',
    'Blue Cross Blue Shield of Florida',
    'Blue Cross Blue Shield of LA',
    'Blue Cross Blue Shield of Texas',
    'Blue Shield of California',
    'Bluebonnet Life Insurance',
    'Booker T. Washington Insurance Company',
    'Boston Mutual Life Ins.',
    'Brighthouse',
    'Brighthouse Financial',
    'Brighthouse Life Insurance Co',
    'Brightpeak Financial',
    'Brokers National Life Assurance Co',
    'BSI Insurance Agency',
    'Burgess Insurance',
    'Business Men\'s Assurance Co.',
    'C A and A Life',
    'C M Life Insurance Company',
    'C Randall Rushings\'s Insurance Company',
    'Caldwell Bankers Richard Ellis',
    'Calfarm Life Insurance Company',
    'Cameron Life Insurance Co.',
    'Canada Life',
    'Capital Security Insurance Co',
    'Capitol American Life Insurance Co',
    'Capitol Marketing Insurance Co',
    'Carmax Life',
    'Carter Insurance',
    'Casual Insurance Group',
    'Casualty Assurance of Chaska, LLC',
    'Catholic Aid Association',
    'Catholic Family Life Ins',
    'Catholic Financial Life',
    'Catholic Fraternal Life',
    'Catholic Holy Family Society',
    'Catholic Knights',
    'Catholic Ladies of Columbia',
    'Catholic Life Ins',
    'Catholic Order of Foresters',
    'Catholic Union of Texas',
    'Catholic United Financial',
    'Catholic Workman',
    'Celtic Insurance Co.',
    'Centennial Life',
    'Central American Life Insurance Co.',
    'Central Benefits National Life Ins Co',
    'Central Life',
    'Central Reserve Life Insurance Company',
    'Central Security Life',
    'Central States of Omaha',
    'Central United Life',
    'Central United Life Insurance Co',
    'Centrian Life Insuranc',
    'Centris Life',
    'Centurion Life Ins Co',
    'Century Life of America',
    'Certified Life Insurance Company',
    'CGU - Avia',
    'CGU Life Insurance',
    'Charter National',
    'Chase Insurance Life and Annuity Company',
    'Chase Life & Annuity',
    'Chase National Life Insurance Co',
    'Cherokee National Life Ins Co',
    'Chesapeake Life Ins Co',
    'Christian Financial Credit Union',
    'Chrysler Insurance',
    'Chubb Life Insurance Co',
    'Church Life Corporation',
    'CICA Life Ins',
    'CiGi Direct Insurance Services, Inc',
    'CIGNA',
    'Cigna Group Insurance',
    'CIGNA Life Ins. Co.',
    'Cincinnati Insurance Company',
    'Cincinnati Life Insurance Co',
    'Citi Life Insurance',
    'Citicorp Life Insurance Company',
    'Citizens Security Life Insurance',
    'Clarica Life',
    'Clarity Insurance',
    'Clement Insurance',
    'Clements Worldwide',
    'Client Services MIR 1',
    'CM Life Insurance Co',
    'CMFG Life',
    'CMFG Life Ins Co',
    'CMG Insurance Group',
    'CNA Assurance Co of Connecticut',
    'CNA Life Ins. Co.',
    'CNO Financial Group',
    'Co-Operators Life Insurance Co',
    'Coastal Life',
    'College Life Ins Co of America',
    'Colonial',
    'Colonial American Life Ins Co',
    'Colonial Life',
    'Colonial Life & Accident Insurance Co',
    'Colonial Penn Life Ins. Co.',
    'Colony Insurance Co',
    'Colorado Bankers Life',
    'Colorado Farm Bureau Mutual Ins Co',
    'Columbia National Insurance Co',
    'Columbia Universal Life Insurance Company',
    'Columbian Life Insurance Co',
    'Columbian Mutual Life Insurance Company',
    'Columbus Life',
    'Combined Ins Co of New York',
    'Combined Insurance',
    'Combined Insurance Co',
    'Comerica Insurance Services',
    'Commercial Union Life Insurance Co',
    'Common Wealth',
    'Common Wealth Service Center',
    'Commonwealth',
    'Commonwealth Annuity',
    'Commonwealth Annuity & Life Ins Company',
    'Commonwealth Annuity and Life Ins Co',
    'Community First Credit Union',
    'Companion Life Insurance Company',
    'Compass Rose Benefits Group',
    'Comprehensive Life',
    'Concord Heritage Life',
    'Confederation Life',
    'Connecticut General',
    'Connecticut General Life',
    'Connecticut Life Ins & Annuity',
    'Connecticut Mutual Life Ins Co',
    'Connecticut National',
    'Connecticut National Life Insurance Co',
    'Conseco/Wilco Life',
    'Consolidated Life Insurance Co',
    'Constitution Life Insurance Co',
    'Consumer Life Insurance',
    'Continental Advantage (HighMark)',
    'Continental Assurance Co',
    'Continental Assurance Company',
    'Continental Casualty Company',
    'Continental General Insurance',
    'Continental General Insurance Co',
    'Continental Life & Accident Company',
    'Continental Life Insurance Co',
    'Continental National Life Ins',
    'Continental Western Co.',
    'Convenant Insurance Co',
    'Cornerstone Assurance Group',
    'COSVI',
    'Cosvi Puerto Rico Life',
    'Cotton States Life',
    'Country Companies',
    'Country Companies Insurance',
    'Country Financial',
    'Country Insurance',
    'Country Investors Life Assurance',
    'Country Life',
    'Country Life Insurance Co',
    'Country Mutual Ins Co Inc',
    'Country Wide Life Insurance',
    'County Investors Assurance',
    'Cova Financial Life Insurance Co.',
    'Covenant Life - Nationwide',
    'Coventry Health and Life Insurance Co',
    'Coverdell and Company Inc',
    'Credit Union Life Insurance',
    'Croation Fraternal Union of America',
    'Crown Life Insurance',
    'CSA Fraternal Life',
    'CU Life Insurance Co',
    'Cultural Group Benefits Company',
    'Cuna Mutual',
    'Dearborn National Life',
    'Degree of Honor',
    'Delaware American Life Ins Co',
    'Delaware Life',
    'Delaware Life (KS)',
    'Delta Life Insurance Co.',
    'Dept of Veteran Affairs',
    'Deseret Mutual Benefit Administration',
    'Desjardins Life',
    'Detroit Mutual',
    'Direct Insurance Co',
    'Direct Response Group',
    'Dixie National Life Ins. Co.',
    'Dream Life',
    'DSTA Life Insurance Co',
    'Durham Life Ins. Co.',
    'E F Hutton Life',
    'E Term',
    'East Carolina Insurance',
    'Eaton Credit Union',
    'EBP Life Insurance Co',
    'EMC National Life Co.',
    'Empire General Life Assurance Company',
    'Empire General Life Assurance Corporation',
    'Employees Modern Life',
    'Encore Insurance Group',
    'Epic Life Insurance Co',
    'Equitable',
    'Equitable Life & Casualty Ins',
    'Equitable Life & Casualty Ins. Co.',
    'Equitable Life of Iowa',
    'Equitable of Colorado Inc',
    'Equitable Reserve Association',
    'Equitable Variable Life',
    'Equitrust Life Ins Co',
    'ERC Life Reinsurance Corp',
    'Erie Family Life Insurance Company',
    'Esurance Customer Service',
    'EULICO',
    'Everance Insurance Company',
    'Everence Association Inc., Life and Health Administration',
    'Everest National Insurance Co',
    'Executive Life Ins Co',
    'Family Benefit',
    'Family Benefit Life Insurance Company',
    'Family Direct',
    'Family Heritage Life Insurance Company of America',
    'Family Life',
    'Family Security Life Insurance Co',
    'Family Service Life Ins Co',
    'Farm Bureau',
    'Farm Bureau Fin Services',
    'Farm Bureau Ins',
    'Farm Bureau Insurance',
    'Farm Bureau Life',
    'Farm Bureau Life Ins',
    'Farm Bureau Life Insurance Company Of Missouri',
    'Farm Bureau of VA',
    'Farm Family Life Insurance Co',
    'Farmer\'s',
    'Farmer\'s Insurance',
    'Farmers',
    'Farmers and Ranchers',
    'Farmers and Ranchers Life Insurance',
    'Farmers and Traders Life Insurance Co',
    'Farmers Insurance',
    'Farmers Life Insurance',
    'Farmers Mutual of TN',
    'Farmers New World Life',
    'Farmers New World Life (variable)',
    'Farmers New World Life Ins. Co.',
    'Farmers New World Life Insurance Co',
    'Farmland  Life Insurance Co',
    'Federaged Mutual Insurance Company',
    'Federal Employeesâ€™ Group Life Insurance Program (FEGLI)',
    'Federal Home Life Insurance Co.',
    'Federal Kemper Life Assurance Company',
    'Federal Life Insurance Company',
    'Federated Insurance Co',
    'Federated Life Insurance Company',
    'Fidelity & Guarantee Life',
    'Fidelity and Guaranty Life Ins Co',
    'Fidelity Bankers Life',
    'Fidelity Federal Life Ins Co',
    'Fidelity Investments Life Insurance Co',
    'Fidelity Life',
    'Fidelity Life Association',
    'Fidelity Mutual Life Ins Co',
    'Fidelity Security Life',
    'Fidelity Security Life Insurance Co',
    'Fidelity Standard',
    'Financial American Life',
    'Financial Centre',
    'Financial Freedom',
    'First Alliance Insurance Co.',
    'First Allmerica Financial Life Ins Co',
    'First Catholic Slovak Ladies Assoc',
    'First Catholic Slovak Union',
    'First Colonial Insurance Company',
    'First Colony',
    'First Command',
    'First Command Financial Planning',
    'First Continental Life and Accidental',
    'First Delaware',
    'First Dominion Mutual Life',
    'First Investors Life Insurance Co',
    'First Life America Corp',
    'First Life Assurance Co',
    'First Penn Pacific Life',
    'First Penn-Pacific',
    'First Sun America',
    'First United American Life Insurance Company',
    'First UNUM Life Insurance Co',
    'First Variable Life Ins Co.',
    'First Variable Life Insurance Co',
    'First Wyoming Capital',
    'Florida Combined Life Insurance Co',
    'Florida Farm Bureau',
    'Focus 10',
    'Ford Life Insurance Company',
    'Foremost Life Insurance Company',
    'Forest T Jones Insurance Co',
    'Foresters',
    'Foresters Life Ins & Annuity Co',
    'Forethought Life Ins Co',
    'Fort Dearborn Life Insurance Co',
    'Fort Dearborn Life Insurance Co.',
    'Fort Dearborn Life Insurance Company',
    'Fortis Benefits Insurance Company',
    'Frankenmuth Insurance',
    'Franklin Insurance',
    'Freedom Life Ins. Co. of America',
    'Freedom Life Insurance Co',
    'Freemont Life Insurance Co',
    'Funeral Consumer Guardian Society Adminitrative Offices',
    'G.E. Capital',
    'G.E. Life & Annuity',
    'Garden State Life Insurance Co',
    'GBU Financial Life',
    'GE Capital Assurance',
    'GE Financial',
    'GE Financial Union Fidelity',
    'GE Life & Annuity',
    'GEBA',
    'Gedsan American Agency, Inc',
    'GEHA',
    'GEICO',
    'GEICO Corporation',
    'GenAmerica Fin Corp',
    'General American',
    'General Casualty',
    'General Electric',
    'General Electric Capital Assurance',
    'General Fidelity Life Insurance Co.',
    'General Life',
    'Generation Life Insurance Company',
    'Genesis Insurance',
    'Geneva Life Insurance Company',
    'Genworth Financial',
    'Geo Care Benefits Group Insurance',
    'Georgia Farm Bureau',
    'Georgia International',
    'Gerber Life Insurance Co',
    'Germania Life Ins Co',
    'Gilsbar Inc',
    'Glacier Insurance of Libby',
    'Gleaner Life Insurance Society',
    'Glenbrook Life & Annuity',
    'Global Atlantic',
    'Global Life & Accident Ins Co',
    'Global Wide Insurance',
    'Globe Life',
    'Globe Life & Accident Ins Co',
    'Globe Life ins Co of NY',
    'Globe Life-Military',
    'Golden American Life Ins Co',
    'Golden American Life Ins. Co. (ING Group)',
    'Golden Rule Insurance Co',
    'Golden Rule Insurance Company',
    'Golden State Mutual Life',
    'Golden West Credit Union',
    'Good Neighbor Insurance',
    'Government Personnel Mutual',
    'GPM Life Ins Co',
    'Grange Life Insurance',
    'Great American Insurance Group',
    'Great American Life Assurance Co of PR',
    'Great American Life Ins Co',
    'Great American Reserve Insurance Co',
    'Great Lakes Mutual Insurance Company',
    'Great Plains Life Assurance Co',
    'Great Southern Life',
    'Great Southern Life Insurance Co',
    'Great West',
    'Great West Life',
    'Great West Life & Annuity Insurance Co',
    'Great Western Life',
    'Great Western Life Ins Co',
    'Great-West Financial',
    'Great-West Life',
    'Great-West Life Assurance Co.',
    'Greater Beneficial Union',
    'Greater Georgia Life Ins Co',
    'Greater Lexington Insurance',
    'Greek Catholic Union of USA',
    'Greenfields LiIfe Ins Co',
    'Greenstone Insurance Co',
    'Grinnell Mutual',
    'Grojean Realty & Insurance Agency',
    'Guarantee Life',
    'Guarantee Reserve Life Ins. Co.',
    'Guarantee Trust Life Insurance Trust',
    'Guaranty Income Life Insurance Co',
    'Guardian',
    'Guardian Life Insurance Company',
    'Guggenheim Life and Annuity',
    'Guidant Insurance Group',
    'GuideOne Insurance',
    'GuideStone Financial Resources',
    'Guild Ins Inc',
    'Gulf Life',
    'Gulfco Life Insurance Co.',
    'Guy\'s Insurance',
    'GVI Insurance',
    'Hamilton Life Assurance Co Ltd',
    'Hanford',
    'Hanover Life Reassurance Co',
    'Harleysville Life Ins. Co.',
    'Hartford - Alliance One',
    'Hartford Life',
    'Hartford Life & Annuity Ins Co',
    'Hartford Life Insurance Company',
    'HARVEY WATT & CO',
    'Harvey Watt and Company',
    'Haven Life',
    'Hawkeye National Life Ins',
    'Hawthorne Insurance Co',
    'Hays Companies',
    'HCC Life Insurance Company',
    'Heartland Insurance Co',
    'Heritage Life',
    'Heritage Union',
    'Hermann Sons Grand Lodge',
    'Hochheim Prairie Insurance',
    'Home Beneficial Life Ins.',
    'Homesteader Life',
    'Horace Mann Ins. Co.',
    'Horizon',
    'Horn & France Insurance',
    'Household Life Insurance Company',
    'Houston National Life',
    'Howard Life Insurance Co.',
    'HRC',
    'HSBC Bank USA, NA',
    'Hudson Specialty Ins Co',
    'Humama',
    'Humana Dental Insurance Co',
    'Humana Life',
    'I.O.F. Foresters',
    'IA American Life',
    'IAC HQ',
    'Idaho Farm Bureau Insurance',
    'IdeaLife Insurance Company',
    'IDS Life Ins Co',
    'IEEE Insurance',
    'IGA-Insurance Group of America',
    'IL Annuity and Insurance Co',
    'Illinois League',
    'Illinois Mutual Life Ins.',
    'Imperial Life Insurance Company',
    'INA',
    'INA Life Insuance CO of NY',
    'Independence Life and Annuity Co',
    'Independent Ins Agents & Brokers of America Inc',
    'Independent Life & Accident Insurance',
    'Independent Order of Foresters',
    'Indiana Farm Bureau Ins',
    'Indiana Farmers Mutual Life',
    'Indianapolis Life',
    'Indianapolis Life Insurance Co',
    'Indianapolis Life Insurance Company',
    'Individual Assurance Company',
    'Industrial Alliance Pacific Insurance and Financial Services',
    'Industrial-Alliance Life Insurance',
    'Infinity Insurance',
    'ING',
    'ING--Golden American Life Ins. Co.',
    'ING-Reliastar-VOYA',
    'ING-SLD-VOYA',
    'ING/VOYA',
    'Institute of Managerial Accountants',
    'Insurance 360',
    'Insurance Administrative Services',
    'Integon Life Insurance Company',
    'Integrated Insurance',
    'Integrity Life Ins Co',
    'Intelliquote',
    'Inter-State Assurance Co',
    'International Life Invest',
    'Interstate Assurance',
    'Invesco Investments Services, Inc.',
    'Investment Life Ins Co of America',
    'Investor\'s Partner Life Ins Co',
    'Investors Fidelity Life',
    'Investors Guaranty Life Insurance Co',
    'Investors Heritage Life Insurance Co',
    'Investors Life',
    'Investors Life Ins Co of Nebraska',
    'Investors Life Ins Co of North America',
    'ITT Hartford International Life',
    'IUAA Insurance Program',
    'J C Penney Life Insurance Co',
    'J G Wentworth Life Insurance',
    'Jackson Naitonal (Formerly Reassure America Life  IL)',
    'Jackson National',
    'Jackson National (Formerly Reassure America Life)',
    'Jackson National (formerly Reassure America TN)',
    'Jackson National (Formerly Reassure America- TX)',
    'Jackson National Life',
    'Jackson National Life Ins',
    'Jackson National Life Ins Co',
    'Jacksonville Life Ins Co',
    'Jamestown Life Insurance Company',
    'JC Penney Life Insurance Company',
    'Jednota',
    'Jefferson National Life Insurance',
    'Jefferson Pilot',
    'Jefferson Pilot Financial',
    'JIME Insurance Co',
    'John Adams Life Ins of America',
    'John Alden Life',
    'John Deere Insurance Company',
    'John Hancock',
    'John Hancock Life Ins Co',
    'K J T',
    'Kanawha Insurance Company',
    'Kansas City Life',
    'Kansas Farm Bureau',
    'Kemper',
    'Kentucky Central Life Inc Company',
    'Kentucky Central Life Insurance Co',
    'Kentucky Farm Bureau',
    'Kentucky Home Life',
    'Key Bank',
    'Key Life Insurance Co',
    'Keystone State Life Ins.',
    'Kilpatrick Life Insurance',
    'Knights of Columbus',
    'KSKJ Life',
    'KY Farm Bureau',
    'Ladder Life',
    'Lafayette Life Ins.',
    'Lamar Life Ins Co',
    'Landmark Insurance',
    'Laurel Life Insurance Company',
    'LBS Insurance Group',
    'Leaders Life Insurance Company',
    'League Life Insurance',
    'Legal & General America',
    'Liberty Bankers Life',
    'Liberty Life Assurance of Boston',
    'Liberty Life Insurance Company (SC)',
    'Liberty Mutual Group Inc',
    'Liberty Mutual Life Assurance',
    'Liberty National',
    'Liberty National Life',
    'Life & Casualty Ins Co of Tennessee',
    'Life Assurance Co of America',
    'Life Help Insurance',
    'Life Ins Co of Virginia',
    'Life Insurance Co of America',
    'Life Insurance Co of NY and Boston',
    'Life Insurance Co of the Southwest',
    'Life Insurance Co of Virginia',
    'Life Insurance Co. of Georgia',
    'Life Insurance Company of Alabama',
    'Life Insurance Company of the Southwest',
    'Life Insurance of Alabama',
    'Life Insurance of North America',
    'Life Investors Life Insurance',
    'Life of Boston Insurance Co.',
    'Life of the South',
    'Life Policyholder Services',
    'Life Ring',
    'Life Secure',
    'Life USA (Universal Benefits Life)',
    'LifeMap',
    'LifeShield National Ins Co',
    'Lighthouse Financial Ntwk',
    'LIIC  (AEGON)',
    'Lincoln',
    'Lincoln American Life Insurance Co',
    'Lincoln Benefit - Alliance One',
    'Lincoln Benefit Life',
    'Lincoln Benefit Life Co',
    'Lincoln Benefit Life Ctr',
    'Lincoln Direct Life',
    'Lincoln Financial',
    'Lincoln Financial Group',
    'Lincoln Heritage',
    'Lincoln Life & Annuity Co of NY',
    'Lincoln Life & Casualty Company',
    'Lincoln Memorial Life Ins Co.',
    'Lincoln Mutual Life and Casualty Ins. Co.',
    'Lincoln National Life',
    'Lincoln Security Life',
    'Lloyd\'s America Inc',
    'LMC Insurance',
    'Londen Insurance Group',
    'London Pacific Life and Annuity',
    'Lone Star Life Insurance Company',
    'Lords of London Petersen International',
    'Loyal American Life Ins Co',
    'Loyal American Life Insurance Company',
    'Loyal Chirstian Benefit Association',
    'Loyalty Life',
    'Loyalty Life Insurance Co',
    'LPL Insurance Agency',
    'LSW LIfe Insurance Company of Southwest',
    'Lumico',
    'Lumico Life Ins Co',
    'Lumico Life Insurance',
    'Luso-American Life Insurance Society',
    'Lutheran Brotherhood',
    'Maccabees Life',
    'Madison Mutual Ins Co',
    'Madison National Insurance Co',
    'Magnolia Life Ins. Co.',
    'Maine Fidelity Life Ins Co',
    'Manhattan Life Insurance Company',
    'Manhattan National Life',
    'Manhattan National Life Insurance Company',
    'Manulife',
    'Mapfre Insurance Co',
    'Marsh U S Consumer',
    'Mass Indemity and Life Ins Co',
    'Mass Mutual',
    'Massachusetts Casualty Insurance',
    'Massachusetts General Life',
    'Massachusetts Indemnity Life',
    'Massachusetts Indemnity Life Ins',
    'Massachusetts Mutual Life Ins Co',
    'Massmark Co',
    'Matrix Direct',
    'Mayflower National Life',
    'MBA (US Postal Service Mutual Benef Assoc)',
    'MBL Life Assurance Co',
    'MBNA',
    'MBNA Insurance Services',
    'MEA Financial Services',
    'Medical Life',
    'Medical Mutual of Ohio',
    'Medico Life',
    'Mega Life & Health',
    'Mega Life & Health Insurance Company',
    'Mega Life and Health',
    'Melife Security Ins Co of LA',
    'Members Life Insurance Co',
    'Memmick Life',
    'Memorial Service Life Ins Co',
    'Mennonite Mutual Aid Association',
    'Mercer Financial Group',
    'Mercer Health & Benefits Admin LLC',
    'Meridian Life Insurance Co',
    'Merit Life Insurance Company',
    'Merrill Lynch Life',
    'Mesa Insuracne Co',
    'Met Life',
    'Met Life - Group',
    'Met Life Ins Co',
    'Met Life Ins Co of CT',
    'Met Life Investors',
    'MetLife',
    'MetLife Investors USA Insurance Co',
    'Metlife Security Ins Co of LA',
    'MetLife Specialized Benefit Resources',
    'Metropolitan Life Ins. Co.',
    'Metropolitan Life Insurance Co',
    'Metropolitan Tower Life Inc',
    'Metropolitan Tower Life Ins Co',
    'Meyer & Associates',
    'Michaels Brothers Insurance',
    'Michigan Life & Health Insurance Guaranty Assoc',
    'Mid-Continent Life Insurance Company',
    'Mid-West Nat Life Ins Comp of Tennessee',
    'Midamerica Mutual Life Ins. Co.',
    'Middlesex Mutual Assurance Company',
    'Midland National',
    'Midland National Life Ins',
    'Midwest Life',
    'MidWest National Life Insurance Co.',
    'Midwest Security Life Ins Co',
    'Midwestern United',
    'Midwestern United Life',
    'MII Life, Inc.',
    'Milico',
    'Military Benefit Association',
    'Milwaukee Life Ins. Co.',
    'Ministers Life',
    'Minnesota Life Ins.',
    'Mission Life Insurance',
    'Mississippi Farm Bureau Casualty Insurance Office',
    'Missouri Farm Bureau',
    'MLI',
    'MLOA',
    'MLOA - MONY Life Insurance Company of America',
    'MML Bay State Life Insurance Co',
    'MOAA Life Plans',
    'Modern Woodmen of America',
    'Momentum Insurance',
    'Monarch Life Insurance Co.',
    'Money Star',
    'Montgomery Insurance Company-Peerless',
    'Monumental',
    'Monumental Life',
    'MONY (an AXA Company)',
    'Moore Corp. Ltd.',
    'MOSERS',
    'Mothe Life Ins. Co.',
    'Motorists Life Ins. Co.',
    'Mountain Life Ins. Co.',
    'Mountain West Farm Bureau Mutual Insurance Company',
    'MSI',
    'Multinational Life Insurance Co',
    'Mutual Benefit Life Assurance Co',
    'Mutual Life Insurance of America (MLOA)',
    'Mutual of America',
    'Mutual of Detroit',
    'Mutual of New York',
    'Mutual of New York (MONY)',
    'Mutual of Omaha',
    'Mutual Savings Life Insurance Co',
    'Mutual Services Insurance Companies',
    'Mutual Trust Life',
    'Myers-Stevens & Toohey Co',
    'NAICO',
    'Nassau Re',
    'NASW Assurance',
    'National American Life Insurance',
    'National Benefit Advisory',
    'National Benefit Life',
    'National Benefit Life Ins Co',
    'National Capital Life Ins Co',
    'National Care Insurance Co',
    'National Catholic Society',
    'National Consumer Trust Co',
    'National Family Care Life Insurance',
    'National Farm Life',
    'National Farmers Union Life',
    'National Fidelity Life',
    'National Financial Security Trust',
    'National General Accident and Health',
    'National Guardian Life',
    'National Health Insurance Co',
    'National Home Life',
    'National Income Life',
    'National Integrity Life Insurance Co',
    'National Life Insurance',
    'National Mutual Benefit Life',
    'National Rifleman Association',
    'National Security Ins Co',
    'National Service Life',
    'National Slovak Society',
    'National States Insurance Co',
    'National Teachers Assoc',
    'National Travelers Life Co',
    'National Union Fire Ins Co',
    'National Western Life Insurance Co.',
    'Nationwide',
    'Nationwide Life',
    'Nationwide Life Insurance',
    'Nationwide Provident',
    'Navy Mutual Aid Assoc',
    'NBFSA',
    'NEA Insurance Operations',
    'NEA Member Benefits',
    'Nebco Life Insurance Company of North America',
    'New England Financial',
    'New Era Life Insurance Company',
    'New Southland Life Ins.',
    'New World Farmers Life',
    'New York Life',
    'New York Life Ins Co',
    'Nodak Mutual Insurance Company',
    'North American',
    'North American Benefit Association',
    'North American Co',
    'North American Company for Life and Health Ins',
    'North American Life and Health',
    'North American Union Life',
    'North Carolina Mutual',
    'North Central Life Ins Co.',
    'North Coast Life Ins',
    'North West Life Assurance Co of America',
    'Northeast Mutual',
    'Northern Life Insurance Co',
    'Northstar Life Insurance Co',
    'Northwest Federal Credit Union',
    'Northwestern Mutual',
    'Northwestern National LIfe Insurance Co',
    'Norwich Union Life Insurance Co',
    'NRA Endorsed Insurance',
    'NSO - Nurses Services Organization',
    'NSS Life',
    'NY SUT Insurance',
    'Occidental Life Insurance Company',
    'Office of Federal Employee Group Life Insurance',
    'Ohio Casualty',
    'Ohio Education Credit Union',
    'Ohio Life Insurance',
    'Ohio Mutual Insurance Grp',
    'Ohio National Life',
    'Ohio State Bar Association (OSBA)',
    'Ohio State Life Ins',
    'Old American Ins. Co.',
    'Old Dominion Life Insurance Co',
    'Old Line Life Insurance Co',
    'Old Mutual Financial',
    'Old Republic Life Insurance Company',
    'Old Surety Life Insurance Company',
    'OM Financial Life Insurance Company',
    'On Line Insurance',
    'Oncor Insurance Company',
    'One America',
    'One National Life',
    'OneAmerica',
    'OOIDA',
    'Order of the Sons of Herman',
    'OSGLI',
    'Owen & Associates, LLC',
    'Oxford Life Insurance Co',
    'Ozark',
    'Ozark National Life',
    'Pacific Corinthia',
    'Pacific Fidelity Life Insurance',
    'Pacific Guardian Life Insurance Co',
    'Pacific Insurance Brokers',
    'Pacific Life',
    'Pacific Life Insurance Co',
    'Pacificare Life & Health',
    'Pan American Assurance Co',
    'Paragon Life',
    'Park Avenue Life',
    'Patriot Life Insurance',
    'Paul Revere Insurance',
    'Pavonia Life Insurance',
    'Pavonia Life Insurance Company of MI',
    'Peachtree Planning',
    'Pearl Carroll & Associates (CSEA)',
    'PEHP',
    'Pekin Financial Life',
    'Pekin Life Insurance Company',
    'Pellerin Life Insurance Co',
    'Pemco Life Insurance Company',
    'Peninsular Life Insurance Co.',
    'Penn Insurance and Annunity Co',
    'Penn Mutual Life Ins.',
    'Penn Treaty Network America',
    'Pennsylvania Life Ins. Co.',
    'Pennsylvania National Guard Associations',
    'Peoples Benefit Life Ins. Co.',
    'Peoples Security (NC)',
    'Petersen International Underwriters',
    'Petersen Lloyd of London',
    'PFL Life Insurance Co',
    'Pharmacists Mutual',
    'Philadelphia American',
    'Philadelphia Life',
    'Philadelphia Life Ins.',
    'PHL Variable Insurance Company',
    'Phoenix Life & Annuity Company',
    'Phoenix Life Ins Co',
    'Phoenix Life/Nassau Re',
    'Phoenix Safe Harbor Ins Co',
    'Phoenix Wealth Management',
    'Physicians Mutual Insurance Company',
    'Pilgrim Insurance Company',
    'Pilot Life Insurance Company',
    'Pioneer American Ins',
    'Pioneer Life Ins Co of IL',
    'Pioneer Mutual Life',
    'Pivot Life Insurance',
    'Plateau Insurance Co',
    'Platinum Choice',
    'Police & Firemen\'s Insurance Associate',
    'Polish Beneficial Assoc',
    'Polish Falcons of America',
    'Polish National Alliance',
    'Polish Women\'s Alliance of America',
    'Praetorian Mutual Life Ins Co',
    'Prairie States Life Ins Co',
    'Preferred Risk Life insurance Co.',
    'Premier Life Ins Co',
    'Presidential Life Insurance Co',
    'Primerica Life Ins Co',
    'Principal Financial',
    'Principal Financial Group',
    'Principal Life Insurance Co',
    'Professional Life and Cas Company',
    'Progressive American Life Ins',
    'Protective Life Insurance Company',
    'Providence Mutual',
    'Provident Life and Casualty Ins Co',
    'Provident Mutual',
    'Providian Life & Health Insurance Co.',
    'Pruco Life Insurance Co',
    'Pruco Life Insurance Company',
    'Prudential',
    'Prudential Financial',
    'Prudential Veterans Group Life',
    'Purple Cross',
    'Pyramid Life Insurance Company',
    'RBC - Liberty Life Insurance',
    'RBC Insurance',
    'Reassurance Company of Hanover',
    'Reassure America Life Ins Co',
    'Regal Life of America Insurance Co',
    'Regis Insurance Co.',
    'Reinusre America LIfe',
    'Reliable Life Insurance Company',
    'Reliance Standard Life Ins. Co.',
    'Reliant Insurance',
    'Reliastar Life Ins Co',
    'Reliastar Life Ins Co (IL)',
    'Reliastar Life Insurance Company of New York',
    'Republic Amercian Life',
    'Republic-Vanguard Life',
    'Reserve National',
    'Reserve Officers Association',
    'Resolution Life Holdings',
    'Resource Life Insurance Company',
    'River\'s Edge Life Insurance',
    'RiverSource',
    'RiverSource Insurance',
    'Rocky Mountain Life Ins. Co',
    'Rorie Insurance Services Inc.',
    'Royal Arcanum',
    'Royal Life Ins Co of America',
    'Royal Maccabees Life Ins. Co.',
    'Royal Maccabees Life Insurance Co',
    'Royal Neighbors of America',
    'Royal Scottish Assurance',
    'Royal State National Insurance Company, Ltd.',
    'Rural Mutual',
    'Rural Security Life Ins Co',
    'Rushmore National Life',
    'Rx Life Insurance Company',
    'Safe Mate Life Insurance Co',
    'SAFECO Life Insurance Co',
    'Sage Life',
    'Sagicor Life Insurance Company',
    'SAMBA',
    'Savers Life Ins Co of America AKA Phoenix Life & Annuity Company',
    'Savings Bank Life Insurance Co',
    'SBL AD&D Ins',
    'SBLI',
    'Schoen Life',
    'Schwend',
    'Scott Credit Union',
    'Scott Insurance',
    'Scottish Widows',
    'Seaboard Life',
    'Seabury & Smith',
    'Sears Life Insurance Company',
    'Secura Life Ins Co',
    'Securian Financial Group',
    'Securian Life Insurance',
    'Security Benefit Life',
    'Security Benefit Life Insurance Co',
    'Security Connecticut',
    'Security Conneticut',
    'Security Equity Life Ins Co.',
    'Security Financial Life - Assurity Life',
    'Security First Life Insurance Company',
    'Security General Life Ins Co',
    'Security Industrial',
    'Security Integon Life',
    'Security Life',
    'Security Life and Trust',
    'Security Life Ins Co of America',
    'Security Life Insurance Company of America',
    'Security Life of Denver',
    'Security Life of Denver Insurance Co',
    'Security Mutual',
    'Security National Life Insurance Co',
    'Security Plan',
    'Security-Connecticut Life (Reliastar)',
    'Select Quote Insurance Company',
    'Selman & Company',
    'Senior Life',
    'Sentinel American Life Ins Co',
    'Sentry Life Insurance Co.',
    'Service Life & Casualty Ins Co',
    'Settlers Life Insurance Company',
    'SGB Insurance Services Inc',
    'SGLI',
    'Shelter Life Insurance Company',
    'Shenandoah Life Ins Co',
    'Signature Life Insurance Co of America',
    'SIU Alumni Insurance Program',
    'Slovene National Benefit Society',
    'SMA Life Assurance Company',
    'Society of Anglican Missonary & Senders; SAMS',
    'Sons of Hermann In the State of Texas',
    'Sons Of Norway',
    'South Carolina Farm Bureau',
    'Southern Farm Bureau',
    'Southern Financial Life',
    'Southern Life and Health Insurance',
    'Southern Security Life Ins. Co.',
    'Southland J',
    'Southland Life',
    'Southland National Ins Corp',
    'Southwestern Life',
    'SPJST',
    'Standard Insurance',
    'Standard Insurance Company',
    'Standard Life and Accident',
    'Standard Life Ins Co of IN',
    'Standard of Oregon',
    'Standard Security Life',
    'Starmount Life Insurance Co.',
    'State Auto Life Insurance',
    'State Employees Credit Union',
    'State Farm Ins',
    'State Farm Ins Cos',
    'State Farm Life Ins Co',
    'State Farm Life Insurance',
    'State Life Fund of the State of Wisconsin',
    'State Life Insurance',
    'State Mutual Ins Co',
    'State Mutual Life Assurance of America',
    'State National Life Insurance Co',
    'State West Life Insurance Company',
    'States General Life Insurance Co',
    'Statesman LIfe Insurance Co',
    'Statetrust Life and Annuities',
    'Steadman Insurance',
    'Sterling Investors Life Ins Co',
    'Sterling Life Insurance',
    'Stonebridge Life',
    'Sun America',
    'Sun Life Financial - DELAWARE LIFE',
    'Sun Pacific Insurance Brokers',
    'SunAmerica',
    'Sunset Life Insurance Company',
    'Surety Life',
    'Swiss Life',
    'Symetra Life Insurance',
    'Synergy',
    'Teachers Ins & Annuity Association',
    'Tennant Co',
    'Tennessee Farm Bureau',
    'Tennessee Farm Life',
    'Texas Farm Bureau Insurance Companies',
    'Texas Life Insurance',
    'Texas Mutual Ins Co',
    'Texas State Bar Insurance TX Trust',
    'The Capitol Life Ins Co',
    'The Chesapeake Life Insurance Co',
    'The College Life Insurance Company of America',
    'The Companies of OneAmerica',
    'The General Agency, Inc',
    'The Hartford Financial Services Grp, Inc',
    'The London Group',
    'The Manufacturers Life Insurance Co',
    'The Mutual Group US',
    'The Mutual Life Insurance Co of NY',
    'The Old Line Life Ins Co (America General)',
    'The Retired Officers Association',
    'The Standard Life Ins Co of NY',
    'Thomas Jefferson Life Insurance Co',
    'Thrivent Financial',
    'Thrivent Financial',
    'TIAA-CREF Life Ins. Co.',
    'Time Ins. Co - Alliance One',
    'TMG Life Ins.',
    'TMG Life Insurance Co',
    'Tom Jones',
    'Torchmark',
    'Tower Life Insurance Company',
    'Trans General Life Insurance Co',
    'Trans World Assurance',
    'Trans World Assurance Group',
    'Trans-Oceanic Life Insurance Company',
    'TransAmerica',
    'Transamerica Advisors',
    'Transamerica Employee Benefits',
    'Transamerica Life',
    'Transamerica Occidental Life Ins Co',
    'Transamerica Premier Life Ins Co',
    'Transtar Insurance Brokers Inc',
    'Travelers Insurance Co',
    'Trident Insurance Agency Company',
    'Trinity Life Ins Co',
    'Triple S-Vida',
    'TruStage',
    'Trustmark - BML',
    'Trustmark Insurance Co.',
    'Trustmark Voluntary Benefit Solutions',
    'U.S.I. Administration',
    'UFB Insurance Company',
    'Ukrainian National Association',
    'Ullico Inc',
    'UniCare',
    'Unified Life Insurance Company',
    'Uniformed Services Benefit Assoc',
    'Uniformed Services Benefit Association',
    'Unimerica Life Ins',
    'Union Bankers',
    'Union Bankers Insurance',
    'Union Benefit Life Insurance Co.',
    'Union Central Life Ins. Co.',
    'Union Fidelity Life',
    'Union Labor Life Insurance',
    'Union Labor Life Insurance Company',
    'Union Mutual',
    'Union National Life Insurance Co',
    'Union Planters Insurance',
    'Union Plus Insurance Program',
    'Union Security - Alliance One',
    'Union Security Ins Co',
    'Union St Jean',
    'Union Standard of America Life Insurance',
    'Unison Credit Union',
    'United American Insurance Co',
    'United Commercial Travelers',
    'United Companies Life',
    'United Farm Family',
    'United Fidelity Life',
    'United Fire Group',
    'United Health & Life Innsurance Co',
    'United Healthcare Golden Rule',
    'United Heritage',
    'United Home Life Insurance Company',
    'United Insurance Co of America',
    'United Insurance Company of America',
    'United International Life Ins Co',
    'United Investors Life Insurance Co',
    'United Life Insurance Co',
    'United Mutual Insurance Co.',
    'United National Life America',
    'United of Omaha',
    'United of Omaha Life',
    'United Olympic Life Ins. Co.',
    'United Pacific Life',
    'United Presidential Life',
    'United Security Assurance Co',
    'United Security Assurance Co.',
    'United Services',
    'United States Life Ins Co',
    'United States Life Insurance Co',
    'United States Life Insurance Company',
    'United Teachers Association',
    'United Transportation Union',
    'United Trust Insurance Co.',
    'Unity Financial Life Insurance Company',
    'Unity Mutual Life',
    'Universal Business Insurance',
    'Universal Guaranty Life Ins.',
    'Universal Underwriters Life',
    'University Family Insurance',
    'UNUM Life Ins Co of America',
    'UNUM Life Ins Group',
    'UNUM Life Insurance Co',
    'Unum Provident Life & Accident Ins Co',
    'US Allianz Service Center',
    'US Bank',
    'US Financial Life',
    'US Financial Life',
    'US Financial Life Insurance Co.',
    'US Life Insurance Co in the City of NY',
    'USA Life 1',
    'USAA Life Insurance Co',
    'USAble Life',
    'USBA',
    'USF & G Insurance Co',
    'USFL - AXA',
    'USFL Insurance Company',
    'USG Annuity & Life Co.',
    'USI Affinity',
    'USI Affinity (New York Life policy)',
    'USI Insurance Services',
    'USLIFE Credit Life',
    'Utica National Life Ins. Co.',
    'Valley Forge Life Ins. Co.',
    'Vantage Ins Co',
    'Vantage West Credit Union',
    'Vantis Life',
    'Vermont Life',
    'Veterans Affairs VA Life Ins',
    'Veterans\' Life Insurance Company',
    'VGLI (Veteran\'s Grp Life Ins)',
    'Virginia Asset Management',
    'Virginia Farm Bureau',
    'Virginia Life',
    'Vista Life Insurance Co',
    'Volunteer State Life Insurance CO',
    'VOYA',
    'Voya Financial',
    'VOYA Financial ( Reliastar Life Insurance )',
    'VOYA/Reliastar',
    'Voyager Insurance Services Inc',
    'Wabash Insurance Associates',
    'Wachovia',
    'Waddell & Reed Financial',
    'WAEPA',
    'WAEPA Insurance',
    'Washington Life Ins Co of America',
    'Washington Mutual Inc',
    'Washington National Ins Co',
    'WAYAH Insurance Group',
    'WAYAH Insurance Group',
    'Wellington Life Insurance Company',
    'Wells Fargo (First Colony)',
    'West Bend Life Ins',
    'West Coast Life Insurance Co',
    'West One Life Insurance Company',
    'Western & Southern Life',
    'Western American Life Ins. Co.',
    'Western Catholic Union',
    'Western Farm Bureau',
    'Western Fraternal Life Association',
    'Western Insurance Specialties',
    'Western National Life Ins Co',
    'Western Reserve Life',
    'Western Reserve Life Assurance Co',
    'Western Reserve Life Assurance of OH',
    'Western Southern Life Assurance Co',
    'Western States Insurance Company',
    'Western United Life Assur Co',
    'Westfield Insurance',
    'Westfield Life Ins. Co.',
    'Wilcac Life Ins Co',
    'Wilco Life Insurance Company',
    'William Penn Life of NY',
    'Wilton Re',
    'Wilton Reassurance Life Co of NY',
    'Wisconsin National Life',
    'Womans Life Insurance Society',
    'Woodall & Hoggle Insurance',
    'Woodbury Financial Services, Inc',
    'Woodland Insurance Agency',
    'Woodmen Accident and Life Ins',
    'Woodmen of the World',
    'Workman\'s Benefit Fund',
    'World Financial Group',
    'World Insurance',
    'World Life Insurance',
    'World Service Life Ins Co',
    'Worldwide Assurance',
    'Wright Insurance',
    'Zale Life Insurance Co',
    'Zander Insurance',
    'Zetron Inc',
    'Zurich American Life',
    'Zurich Life'];
}
